import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';
import Modal from './Modal';
import EditContactModal from './EditContactModal';

export default function ContactModal({ isOpen, onClose, contactId }) {
    const { userEmail } = useAddin();
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (!isOpen || !contactId) return;

        let isMounted = true;
        setLoading(true);
        setError(null);

        webhook.getContact({ userEmail, contactId })
            .then(res => {
                if (isMounted) {
                    setContact(res.data || res.contact || res);
                    setLoading(false);
                }
            })
            .catch(err => {
                console.error("Failed to fetch contact details:", err);
                if (isMounted) {
                    setError(err.message);
                    setLoading(false);
                }
            });

        return () => { isMounted = false; };
    }, [isOpen, contactId, userEmail]);

    if (!isOpen) return null;

    if (isEditing && contact) {
        return (
            <EditContactModal
                isOpen={isEditing}
                onClose={() => setIsEditing(false)}
                contactData={contact}
                onSuccess={(updatedContact) => {
                    setContact(updatedContact);
                    setIsEditing(false);
                }}
            />
        );
    }

    const renderField = (label, value) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return null;
        return (
            <div className="info-row" style={{ marginBottom: '8px' }}>
                <div className="info-key" style={{ minWidth: '120px' }}>{label}</div>
                <div className="info-val">{value}</div>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Contact Details">
            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a' }}>Loading contact details...</div>
            ) : error ? (
                <div style={{ color: 'var(--orange)', padding: '10px' }}>Error: {error}</div>
            ) : contact ? (
                <div className="addin-body" style={{ padding: '0', overflowY: 'auto' }}>
                    <div className="contact-hero" style={{ textAlign: 'center', padding: '20px 0', borderBottom: '1px solid var(--border-color)' }}>
                        <div className="mh-title" style={{ fontSize: '20px' }}>{contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()}</div>
                        <div className="mh-meta" style={{ justifyContent: 'center', marginTop: '8px' }}>
                            <span className="badge">{contact.type || 'Person'}</span>
                        </div>
                        <button 
                            style={{ marginTop: '16px' }}
                            className="primary-btn"
                            onClick={() => setIsEditing(true)}
                        >
                            Edit Contact
                        </button>
                    </div>

                    <div className="info-block" style={{ marginTop: '16px', border: 'none', background: 'transparent', padding: '0' }}>
                        {renderField('Title', contact.title)}
                        {renderField('First Name', contact.first_name)}
                        {renderField('Middle Name', contact.middle_name)}
                        {renderField('Last Name', contact.last_name)}
                        {renderField('Prefix', contact.prefix)}
                        {renderField('Initials', contact.initials)}
                        
                        {contact.email_addresses?.map((email, i) => 
                            renderField(`Email ${i + 1}`, `${email.address} ${email.primary ? '(Primary)' : ''} ${email.name ? `[${email.name}]` : ''}`)
                        )}
                        
                        {contact.phone_numbers?.map((phone, i) => 
                            renderField(`Phone ${i + 1}`, `${phone.number} ${phone.primary ? '(Primary)' : ''} ${phone.name ? `[${phone.name}]` : ''}`)
                        )}

                        {contact.addresses?.map((address, i) => 
                            renderField(`Address ${i + 1}`, `${[address.street, address.city, address.province, address.postal_code, address.country].filter(Boolean).join(', ')} ${address.primary ? '(Primary)' : ''} ${address.name ? `[${address.name}]` : ''}`)
                        )}

                        {contact.web_sites?.map((site, i) => 
                            renderField(`Website ${i + 1}`, <span><a href={site.address} target="_blank" rel="noreferrer" className="link">{site.address}</a> {site.primary ? '(Primary)' : ''} {site.name ? `[${site.name}]` : ''}</span>)
                        )}

                        {contact.company?.name && renderField('Company', contact.company.name)}
                        {contact.date_of_birth && renderField('Date of Birth', contact.date_of_birth)}
                        {renderField('Sales Tax #', contact.sales_tax_number)}
                        {renderField('LEDES Client ID', contact.ledes_client_id)}
                        {renderField('Clio Connect Email', contact.clio_connect_email)}
                        {renderField('Is Client', contact.is_client ? 'Yes' : 'No')}
                    </div>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a' }}>No contact found.</div>
            )}
        </Modal>
    );
}
